// TopBar — fixed header that starts at the sidebar's right edge and
// extends to the right of the viewport. Visually transparent: shares
// the page background so the only chrome is the sidebar.
//
// Left:    Company name (from authStore — flexes)
// Right:   Notification bell, identity (avatar + name + role), logout.
//
// The brand wordmark, Home link, and QuickLinks menu live in the
// sidebar header now — not here.
//
// Wave G adds an impersonation banner that stacks ABOVE the normal
// header when impersonating. Wave I moved the impersonation state into
// `useSuperAdminStore` (separate from the tenant `useAuthStore`).

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, LayoutGrid, LogOut, ShieldAlert } from 'lucide-react';
import { ModuleSwitcher } from './ModuleSwitcher/ModuleSwitcher';
import { useAuthStore } from '../../store/authStore';
import { useSuperAdminStore } from '../../store/superAdminStore';
import { useCollectionsNotifStore } from '../../store/collectionsNotifStore';
import { useAuth } from '../../auth/useAuth';
import { useSuperAuth } from '../../auth/useSuperAuth';
import { Avatar } from '../ui/Avatar/Avatar';
import { Badge } from '../ui/Badge/Badge';
import { Popover } from '../ui/Popover/Popover';
import { cn } from '../../utils/cn';
import styles from './TopBar.module.css';

export function TopBar() {
  const user = useAuthStore((s) => s.user);
  const receiptsNeedingAction = useCollectionsNotifStore((s) => s.receiptsNeedingAction);
  const cancellationRequests = useCollectionsNotifStore((s) => s.cancellationRequests);
  const completedAfterCancellation = useCollectionsNotifStore((s) => s.completedAfterCancellation);
  const isBellActive = useCollectionsNotifStore((s) => s.isActive);
  const totalUnread = receiptsNeedingAction + cancellationRequests + completedAfterCancellation;
  const [bellOpen, setBellOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const isImpersonating = useSuperAdminStore((s) => s.isImpersonating);
  const impersonatedCompany = useSuperAdminStore((s) => s.impersonatedCompany);
  const stopImpersonation = useSuperAdminStore((s) => s.stopImpersonation);
  const setAuthStoreAnonymous = useAuthStore((s) => s.setAnonymous);
  const superAdmin = useSuperAdminStore((s) => s.admin);
  const isSuperAuthenticated = useSuperAdminStore((s) => s.isAuthenticated);
  const { logout: staffLogout } = useAuth();
  const { logout: superLogout } = useSuperAuth();
  const navigate = useNavigate();

  // Pure super-admin view (signed in via /super/login, not impersonating).
  const isPureSuperView = isSuperAuthenticated && !isImpersonating;

  async function handleLogout() {
    if (isPureSuperView) {
      await superLogout();
      navigate('/super/login', { replace: true });
      return;
    }
    await staffLogout();
    navigate('/login', { replace: true });
  }

  function handleStopImpersonation() {
    stopImpersonation();
    // The tenant auth store was hydrated for the impersonated tenant — the
    // super admin is no longer that tenant, so clear it.
    setAuthStoreAnonymous();
    navigate('/super/companies');
  }

  return (
    <>
      {isImpersonating && impersonatedCompany ? (
        <div role="alert" className={styles.impersonationBanner}>
          <ShieldAlert size={18} aria-hidden />
          <span className={styles.impersonationText}>
            <strong>Impersonating:</strong> {impersonatedCompany.name}
          </span>
          <button
            type="button"
            className={styles.impersonationStop}
            onClick={handleStopImpersonation}
          >
            Stop Impersonating
          </button>
        </div>
      ) : null}

      <header role="banner" className={styles.bar}>
        <div className={styles.spacer}>
          <button
            type="button"
            className={styles.switcherTrigger}
            onClick={() => setSwitcherOpen(true)}
            aria-label="Switch module"
            title="Switch module"
          >
            <LayoutGrid size={18} />
          </button>
          {isImpersonating && impersonatedCompany ? (
            <span
              className={styles.companyName}
              title={`Impersonating ${impersonatedCompany.name}`}
            >
              {impersonatedCompany.name}
            </span>
          ) : isPureSuperView ? (
            <span className={styles.companyName} title="Tricivo Platform Admin">
              Tricivo Platform Admin
            </span>
          ) : user?.company_name ? (
            <span className={styles.companyName} title={user.company_name}>
              {user.company_name}
            </span>
          ) : null}
        </div>

        <div className={styles.actions}>
          {isBellActive && (
            <Popover
              open={bellOpen}
              onOpenChange={setBellOpen}
              placement="bottom"
              ariaLabel="Notifications"
              className={styles.notifPanel}
              trigger={
                <button
                  type="button"
                  className={styles.bell}
                  aria-label={
                    totalUnread > 0
                      ? `${totalUnread} notifications needing attention`
                      : 'Notifications'
                  }
                >
                  <Bell size={18} aria-hidden />
                  {totalUnread > 0 ? (
                    <span className={styles.badge} aria-hidden>
                      {totalUnread > 99 ? '99+' : totalUnread}
                    </span>
                  ) : null}
                </button>
              }
            >
              <div className={styles.notifHeader}>Notifications</div>
              <Link
                to="/collections/receipts"
                className={styles.notifRow}
                onClick={() => setBellOpen(false)}
              >
                <span>Receipts needing action</span>
                <span
                  className={cn(
                    styles.notifCount,
                    receiptsNeedingAction === 0 && styles.notifCountZero,
                  )}
                >
                  {receiptsNeedingAction}
                </span>
              </Link>
              <Link
                to="/collections/cancellations"
                className={styles.notifRow}
                onClick={() => setBellOpen(false)}
              >
                <span>Cancellation requests</span>
                <span
                  className={cn(
                    styles.notifCount,
                    cancellationRequests === 0 && styles.notifCountZero,
                  )}
                >
                  {cancellationRequests}
                </span>
              </Link>
              <Link
                to="/collections/cancellations?tab=conflicts"
                className={styles.notifRow}
                onClick={() => setBellOpen(false)}
              >
                <span>Completed after cancellation</span>
                <span
                  className={cn(
                    styles.notifCount,
                    completedAfterCancellation === 0 && styles.notifCountZero,
                  )}
                >
                  {completedAfterCancellation}
                </span>
              </Link>
            </Popover>
          )}

          {isImpersonating && impersonatedCompany ? (
            <div className={styles.user}>
              <Avatar name={impersonatedCompany.name} size="sm" />
              <div className={styles.userInfo}>
                <span className={styles.userName}>{impersonatedCompany.name}</span>
                <Badge variant="warning">impersonated</Badge>
              </div>
            </div>
          ) : isPureSuperView && superAdmin ? (
            <div className={styles.user}>
              <Avatar name={superAdmin.name || 'Platform Admin'} size="sm" />
              <div className={styles.userInfo}>
                <span className={styles.userName}>{superAdmin.name || 'Platform Admin'}</span>
                <Badge variant="muted">super</Badge>
              </div>
            </div>
          ) : user ? (
            <div className={styles.user}>
              <Avatar name={user.name} size="sm" />
              <div className={styles.userInfo}>
                <span className={styles.userName}>{user.name}</span>
                <Badge variant="muted">{user.role}</Badge>
              </div>
            </div>
          ) : null}

          <button type="button" className={styles.logout} onClick={handleLogout}>
            <LogOut size={14} aria-hidden />
            Logout
          </button>
        </div>
      </header>

      <ModuleSwitcher isOpen={switcherOpen} onClose={() => setSwitcherOpen(false)} />
    </>
  );
}
